import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Easing,
  Dimensions,
  Image,
  Platform,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import FastImage from "react-native-fast-image";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { AppIcons } from "../../assets/images";
import { AppTheme } from "../../utils/theme";
import LinearGradient from "react-native-linear-gradient";
import { formatCoins, interstitial, LUCKY_SPIN, goBackOrHome } from "../../utils/contants";
import { setAppOpenAdPaused } from "../../utils/useOpenAppAd";
import { AdEventType } from "react-native-google-mobile-ads";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ROUTE_NAME } from "../../utils/routes";
import api from "../../utils/axiosinstance";
import axios from "axios";
import AnimatedLottieModal from "../../components/global/AnimatedLottieModal";
import { useAppSelector } from "../../redux/storeHook";
import Sound from "react-native-sound";
import { userAction } from "../../redux/reducers/user";

const screenWidth =
  Platform.OS === "web"
    ? Math.min(Dimensions.get("window").width, 480)
    : Dimensions.get("window").width;

// Precise geometry matching SpinBack.png (1095x1235) and Spin.png (930x930)
const FRAME_WIDTH = Math.min(screenWidth * 0.88, 350);
const FRAME_HEIGHT = FRAME_WIDTH * (1235 / 1095);
const WHEEL_SIZE = FRAME_WIDTH * (930 / 1095);
const WHEEL_TOP = FRAME_HEIGHT * 0.5566 - WHEEL_SIZE / 2;
const WHEEL_LEFT = (FRAME_WIDTH - WHEEL_SIZE) / 2;
const POINTER_TOP = WHEEL_TOP - 22;

const IMAGE_OFFSET = 22.5; // Half segment for 8 segments
const FREE_SPIN_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_AD_SPINS_PER_DAY = 10;

/* 🎁 Rewards - 8 segments matching Spin.png art */
type Reward = {
  label: string;
  value: number;
};

const rewards: Reward[] = [
  { label: "10", value: 10 },
  { label: "100", value: 100 },
  { label: "50", value: 50 },
  { label: "200", value: 200 },
  { label: "20", value: 20 },
  { label: "500", value: 500 },
  { label: "0", value: 0 },
  { label: "1000", value: 1000 },
];

const SEGMENT_ANGLE = 360 / rewards.length;

const LuckySpinScreen: React.FC = () => {
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const [isSpinning, setIsSpinning] = useState(false);
  const [coins, setCoins] = useState("0");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [pendingAd, setPendingAd] = useState(false);
  const [animationModal, setAnimationModal] = useState(false);
  const { userData } = useAppSelector((s) => s.user);
  const { isSoundOn } = useAppSelector((state) => state.user);

  const navigation = useNavigation<NativeStackNavigationProp<any>>();

  // Sound reference
  const spinSound = useRef<Sound | null>(null);

  /* Preload spin sound */
  useEffect(() => {
    Sound.setCategory("Playback");
    spinSound.current = new Sound("spin.mp3", Sound.MAIN_BUNDLE, (error) => {
      if (error) {
        console.log("Failed to load spin sound", error);
      }
    });

    return () => {
      if (spinSound.current) {
        spinSound.current.release();
      }
    };
  }, []);

  /* Get local date string YYYY-MM-DD */
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /* Load last free spin time */
  useEffect(() => {
    const loadLastFreeSpin = async () => {
      try {
        const lastFreeTimeStr = await AsyncStorage.getItem("lastFreeSpin");
        if (lastFreeTimeStr) {
          const lastFreeTime = parseInt(lastFreeTimeStr, 10);
          const now = Date.now();
          const elapsed = now - lastFreeTime;
          const remainingMs = Math.max(0, FREE_SPIN_COOLDOWN_MS - elapsed);
          setRemainingTime(Math.floor(remainingMs / 1000));
        }
      } catch (error) {
        console.error("Error loading last free spin time:", error);
      }
    };
    loadLastFreeSpin();
  }, []);

  /* Timer effect */
  useEffect(() => {
    let interval: any = null;
    if (remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [remainingTime]);

  /* Format remaining time */
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const unsubscribeLoaded = interstitial.addAdEventListener(
      AdEventType.LOADED,
      () => {
        if (pendingAd) {
          setPendingAd(false);
          setAppOpenAdPaused(true);
          interstitial.show();
        }
      }
    );
    const unsubscribeClosed = interstitial.addAdEventListener(
      AdEventType.CLOSED,
      async () => {
        setAppOpenAdPaused(false, 1000);
        try {
          const today = getTodayDate();
          const storedCountStr = await AsyncStorage.getItem(
            `ad_spins_${today}`
          );
          const count = storedCountStr ? parseInt(storedCountStr, 10) : 0;
          const newCount = count + 1;
          await AsyncStorage.setItem(`ad_spins_${today}`, newCount.toString());
        } catch (error) {
          console.error("Error incrementing ad spins:", error);
        }
        doSpin();
        interstitial.load();
      }
    );
    interstitial.load();
    return () => {
      unsubscribeLoaded();
      unsubscribeClosed();
    };
  }, []);

  /* 🎡 Core spin logic */
  const doSpin = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setSelectedIndex(null);

    if (isSoundOn && spinSound.current) {
      spinSound.current.play();
    }

    const randomIndex = Math.floor(Math.random() * rewards.length);
    const selectedReward = rewards[randomIndex];

    const segmentCenter =
      randomIndex * SEGMENT_ANGLE + SEGMENT_ANGLE / 2 + IMAGE_OFFSET;

    const targetAngle = (360 - segmentCenter + 360) % 360;
    const spinTo = 360 * 5 + targetAngle;

    Animated.timing(rotateAnim, {
      toValue: spinTo,
      duration: 4500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      if (isSoundOn && spinSound.current) {
        spinSound.current.stop();
      }

      rotateAnim.setValue(spinTo % 360);
      setSelectedIndex(randomIndex);
      setIsSpinning(false);

      if (selectedReward.value > 0) {
        setCoins(selectedReward.value.toString());
        coinApiRequest(selectedReward.value);
      } else {
        setCoins("0");
        setAnimationModal(true);
      }
    });
  };

  const handleWatchAd = () => {
    if (interstitial.loaded) {
      setAppOpenAdPaused(true);
      interstitial.show();
    } else {
      setPendingAd(true);
      interstitial.load();
    }
  };

  /* 🎡 Main spin entry point */
  const spinWheel = async () => {
    if (!userData?.access_token) {
      Alert.alert("Login", "To play Lucky Spin, you need to log in first.", [
        {
          text: "Login",
          onPress: () => {
            navigation.navigate(ROUTE_NAME.LoginScreen);
          },
        },
        {
          text: "Cancel",
          style: "cancel",
        },
      ]);
      return;
    }

    setAnimationModal(false);
    if (isSpinning) return;

    try {
      const lastFreeTimeStr = await AsyncStorage.getItem("lastFreeSpin");
      const now = Date.now();
      const lastFreeTime = lastFreeTimeStr ? parseInt(lastFreeTimeStr, 10) : 0;
      const canFreeSpin = now - lastFreeTime > FREE_SPIN_COOLDOWN_MS;

      if (canFreeSpin) {
        doSpin();
        await AsyncStorage.setItem("lastFreeSpin", now.toString());
        setRemainingTime(24 * 60 * 60);
      } else {
        const today = getTodayDate();
        const storedCountStr = await AsyncStorage.getItem(`ad_spins_${today}`);
        const count = storedCountStr ? parseInt(storedCountStr, 10) : 0;

        if (count >= MAX_AD_SPINS_PER_DAY) {
          Alert.alert(
            "Daily Limit Reached",
            "You have reached the maximum number of ad spins for today. Please come back tomorrow for your free spin!"
          );
          return;
        }

        Alert.alert(
          "Watch Ad for Spin",
          "Watch an Ad to unlock a spin!",
          [
            {
              text: "Watch Ad",
              onPress: handleWatchAd,
            },
            { text: "Cancel", style: "cancel" },
          ],
          { cancelable: true }
        );
      }
    } catch (error) {
      console.error("Error in spinWheel:", error);
    }
  };

  const rotateInterpolation = rotateAnim.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  const coinApiRequest = async (coin: number) => {
    try {
      const body = {
        coinsToAdd: coin,
      };
      const res = await api.post(LUCKY_SPIN, body);
      if (res.data?.status) {
        setCoins(String(res.data.coinsAdded || coin));
        setAnimationModal(true);
      } else {
        setCoins(String(coin));
        setAnimationModal(true);
      }
    } catch (err: any) {
      setCoins(String(coin));
      setAnimationModal(true);
    }
  };

  return (
    <FastImage
      source={AppIcons.SpinBackground1}
      style={styles.container}
      resizeMode="cover"
    >
      {/* 🔙 Back */}
      <TouchableOpacity
        onPress={() => {
          userAction.setTouchSound(true);
          goBackOrHome(navigation);
        }}
        style={styles.backIcon}
      >
        <Image
          source={AppIcons.BackIcon}
          style={{ width: 48, height: 38 }}
          resizeMode={"contain"}
        />
      </TouchableOpacity>

      <SafeAreaView style={styles.content}>
        {/* 🎉 Title Ribbon */}
        <View style={styles.headerContainer}>
          <FastImage
            source={AppIcons.Luck}
            style={styles.headerImage}
            resizeMode="contain"
          />
        </View>

        {/* 🎡 Wheel Center Stack */}
        <View style={styles.wheelWrapper}>
          {/* Gold outer frame containing "LUCKY" and "SPIN" arch */}
          <FastImage
            source={AppIcons.SpinBackground}
            style={styles.wheelBackimg}
            resizeMode="contain"
          />

          {/* Rotating Wheel */}
          <Animated.View
            style={[
              styles.wheelContainer,
              { transform: [{ rotate: rotateInterpolation }] },
            ]}
          >
            <FastImage
              source={AppIcons.Spin}
              style={styles.wheelImage}
              resizeMode="contain"
            />

            {/* 🔢 Exact Coin Counts matching reference screenshot */}
            <Text style={styles.count1000}>1000</Text>
            <Text style={styles.count100}>100</Text>
            <Text style={styles.count200}>200</Text>
            <Text style={styles.count500}>500</Text>
          </Animated.View>

          {/* 📍 Pointer Pin */}
          <View style={styles.pointerContainer}>
            <Image
              source={AppIcons.Point}
              style={styles.pointerImage}
              resizeMode="contain"
            />
          </View>
        </View>

        {/* ⏰ Timer Display */}
        {remainingTime > 0 ? (
          <View style={styles.timerContainer}>
            <Text style={styles.timerText}>
              Next Free Spin In: {formatTime(remainingTime)}
            </Text>
          </View>
        ) : (
          <View style={{ height: 22 }} />
        )}

        {/* 🔘 Spin Button */}
        <TouchableOpacity
          activeOpacity={0.85}
          style={styles.spinButtonWrapper}
          onPress={() => {
            userAction.setTouchSound(true);
            spinWheel();
          }}
          disabled={isSpinning}
        >
          <LinearGradient
            colors={["#FFB300", "#FF8F00", "#FF6F00"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.spinBtn}
          >
            <Text style={styles.startText}>SPIN</Text>
          </LinearGradient>
        </TouchableOpacity>
      </SafeAreaView>

      <AnimatedLottieModal
        coins={coins}
        visible={animationModal}
        onClose={() => setAnimationModal(false)}
      />
    </FastImage>
  );
};

export default LuckySpinScreen;

/* ================== STYLES ================== */

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 30,
    paddingBottom: 40,
  },
  backIcon: {
    position: "absolute",
    top: 50,
    right: 0,
    zIndex: 999,
    elevation: 999,
  },
  coinBadgeContainer: {
    position: "absolute",
    top: 50,
    left: 15,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 30,
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 999,
    elevation: 999,
    paddingRight: 6,
  },
  coinIcon: {
    width: 32,
    height: 36,
    marginLeft: -6,
  },
  countContainer: {
    paddingHorizontal: 10,
  },
  countText: {
    color: AppTheme.colors.white,
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_MEDIUM,
  },
  plusIcon: {
    width: 26,
    height: 26,
  },
  headerContainer: {
    alignItems: "center",
    marginTop: 10,
  },
  headerImage: {
    width: 270,
    height: 70,
  },
  wheelWrapper: {
    width: FRAME_WIDTH,
    height: FRAME_HEIGHT,
    position: "relative",
    alignSelf: "center",
    marginVertical: 5,
  },
  wheelBackimg: {
    position: "absolute",
    width: FRAME_WIDTH,
    height: FRAME_HEIGHT,
    top: 0,
    left: 0,
  },
  wheelContainer: {
    position: "absolute",
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
    top: WHEEL_TOP,
    left: WHEEL_LEFT,
    justifyContent: "center",
    alignItems: "center",
  },
  wheelImage: {
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
  },
  count1000: {
    position: "absolute",
    top: WHEEL_SIZE * 0.28,
    alignSelf: "center",
    color: "#FFFFFF",
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    textAlign: "center",
    textShadowColor: "rgba(0,0,0,0.8)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  count100: {
    position: "absolute",
    right: WHEEL_SIZE * 0.27,
    top: WHEEL_SIZE * 0.5 - 10,
    color: "#FFFFFF",
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    textAlign: "center",
    transform: [{ rotate: "90deg" }],
    textShadowColor: "rgba(0,0,0,0.8)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  count200: {
    position: "absolute",
    bottom: WHEEL_SIZE * 0.28,
    alignSelf: "center",
    color: "#FFFFFF",
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    textAlign: "center",
    transform: [{ rotate: "180deg" }],
    textShadowColor: "rgba(0,0,0,0.8)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  count500: {
    position: "absolute",
    left: WHEEL_SIZE * 0.27,
    top: WHEEL_SIZE * 0.5 - 10,
    color: "#FFFFFF",
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    textAlign: "center",
    transform: [{ rotate: "270deg" }],
    textShadowColor: "rgba(0,0,0,0.8)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  pointerContainer: {
    position: "absolute",
    top: POINTER_TOP,
    left: (FRAME_WIDTH - 48) / 2,
    zIndex: 100,
  },
  pointerImage: {
    width: 48,
    height: 52,
  },
  spinButtonWrapper: {
    alignSelf: "center",
    marginBottom: 20,
  },
  spinBtn: {
    width: 220,
    height: 52,
    borderRadius: 26,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: "#FFE066",
  },
  startText: {
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 30,
    color: AppTheme.colors.white,
    textAlign: "center",
    textShadowColor: "rgba(0,0,0,0.4)",
    textShadowOffset: { width: 1, height: 2 },
    textShadowRadius: 2,
    letterSpacing: 1.5,
  },
  timerContainer: {
    alignItems: "center",
    marginVertical: 4,
  },
  timerText: {
    color: AppTheme.colors.white,
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    textAlign: "center",
    textShadowColor: "rgba(0,0,0,0.6)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
});
